import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { RestartConfirmationDialog } from '@/components/dialogs/RestartConfirmationDialog';
import { DocumentChat } from '@/components/main-page/document-chat';
import { Sidebar } from '@/components/main-page/sidebar';
import { StartDialog } from '@/components/main-page/start-dialog';
import { MinimalTiptapEditor } from '@/components/minimal-tiptap';
import { type CaseData, useCaseContext } from '@/components/providers/case-context-provider';
import { useEditorContext } from '@/components/providers/editor-context-provider';
import { useMessageContext } from '@/components/providers/message-context-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { WORKFLOWS } from '@/lib/ai/mastra';
import type { DocumentInfo } from '@/lib/analytics/types';
import { useProcessWorkflow } from '@/lib/hooks/useProcessWorkflow';
import type { FileResult } from '@/schemas/fileResult';

export const Route = createFileRoute('/(main)/_mainLayout/')({
  component: AlexAiPage,
});

function AlexAiPage() {
  const {
    caseData,
    setCaseData,
    upsertAnalyse,
    updateFile,
    resetCaseData,
    updateAdditionalCaseInformation,
  } = useCaseContext();
  const { addUserMessage, clearMessages } = useMessageContext();
  const {
    editorValue,
    setEditorValue,
    setIsGenerating,
    currentDisplayedContext,
    setCurrentDisplayedContext,
    setHasAborted,
    stopWorkflow,
    resetAllEditorStates,
    isGenerating,
  } = useEditorContext();

  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);
  const [isReopeningStartDialog, setIsReopeningStartDialog] = useState(false);
  const [isRestartConfirmationDialogOpen, setIsRestartConfirmationDialogOpen] = useState(false);

  const { caseTitle, setCaseTitle, startWorkflow } = useProcessWorkflow('mainLayout.index');

  useEffect(() => {
    setIsStartDialogOpen(true);
  }, []);

  const getCurrentDocumentInfo = useCallback((): DocumentInfo | null => {
    const currentId = currentDisplayedContext.id;

    if (!currentId) return null;

    const analysis = caseData.analyses.find((a) => a.id === currentId);
    if (analysis) {
      return {
        title: analysis.title,
        type: analysis.analyseType,
        category: 'analysis',
      };
    }

    const file = caseData.files.find((f) => f.id === currentId);
    if (file) {
      return {
        title: file.filename,
        type: 'file',
        category: 'file',
      };
    }

    if (currentId === caseData.additionalCaseInformation.id) {
      return {
        title: 'Aanvullende dossier informatie',
        type: 'additional_info',
        category: 'additional_info',
      };
    }

    return null;
  }, [
    currentDisplayedContext.id,
    caseData.analyses,
    caseData.files,
    caseData.additionalCaseInformation,
  ]);

  const updateCurrentDisplayedDocument = useCallback(
    (content: string) => {
      const currentId = currentDisplayedContext.id;

      if (!currentId) return null;

      const analysis = caseData.analyses.find((a) => a.id === currentId);
      if (analysis) {
        upsertAnalyse({ ...analysis, result: content });
      }

      const file = caseData.files.find((f) => f.id === currentId);
      if (file) {
        updateFile(file.id, {
          ...file,
          redactedFile: file.redactedFile ? { ...file.redactedFile, text: content } : null,
        });
      }

      if (currentId === caseData.additionalCaseInformation.id) {
        updateAdditionalCaseInformation(content);
      }

      setCurrentDisplayedContext({ ...currentDisplayedContext, content });

      return null;
    },
    [
      currentDisplayedContext.id,
      caseData.analyses,
      caseData.files,
      caseData.additionalCaseInformation,
    ],
  );

  const generateDocument = async ({
    data,
    userAction,
  }: {
    data: CaseData;
    userAction?: string;
  }) => {
    setCaseData(data);
    setIsGenerating(true);
    setHasAborted(false);

    // If the user
    if (userAction !== undefined) {
      await addUserMessage({ content: userAction });
    }

    const redactedFiles = data.files
      .map((file) => file.redactedFile)
      .filter((redactedFile) => redactedFile !== null);

    if (redactedFiles.length === 0 && !data.additionalCaseInformation) {
      return;
    }

    const assistantInput: FileResult[] = [
      ...redactedFiles,
      {
        filename: 'additional_case_information',
        label: 'additional_case_information',
        text: data.additionalCaseInformation.value || '',
      },
    ];

    if (!Object.keys(WORKFLOWS).includes(data.selectedValue)) {
      setIsGenerating(false);
      return;
    }

    startWorkflow(data.selectedValue, assistantInput);
  };

  // INFO: we don't remove any uploaded files in Firestore here
  // because maybe we will want that information afterwards, even if
  // the case doesn't exist anymore for the user.
  // If we do want to remove them, we can call `removeFilesFromFileProcessing`
  // from `lib/file/common.ts`
  const startNewProcess = async () => {
    if (isGenerating) {
      await stopWorkflow();
    }

    resetAllEditorStates();
    clearMessages();
    resetCaseData();
    setIsRestartConfirmationDialogOpen(false);
    setCaseTitle(null);

    setIsReopeningStartDialog(true);
    setIsStartDialogOpen(true);
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <TooltipProvider>
        <div className="flex min-h-screen w-full rounded-xl bg-white">
          <Sidebar
            startNewProcess={() => setIsRestartConfirmationDialogOpen(true)}
            onGenerateDocument={generateDocument}
            caseTitle={caseTitle}
          />

          <div className="flex-1">
            <MinimalTiptapEditor
              showChatPanel={false}
              immediatelyRender={false}
              value={editorValue}
              onChange={(e) => {
                const content = e?.toString() || '';
                setEditorValue(content);
                updateCurrentDisplayedDocument(content);
              }}
              output="html"
              autofocus={false}
              editable={true}
              editorClassName="focus:outline-hidden"
              currentDocument={getCurrentDocumentInfo()}
            />
          </div>

          <DocumentChat onGenerateDocument={generateDocument} />
        </div>

        <StartDialog
          isOpen={isStartDialogOpen}
          isReopeningStartDialog={isReopeningStartDialog}
          onOpenChange={setIsStartDialogOpen}
          onGenerateDocument={generateDocument}
        />
        <RestartConfirmationDialog
          isOpen={isRestartConfirmationDialogOpen}
          onOpenChange={setIsRestartConfirmationDialogOpen}
          onAcceptClick={startNewProcess}
        />
      </TooltipProvider>
    </Suspense>
  );
}
